<template>
  <div>
    <div class="map-wrapper" :id="mapId"></div>
    <div id="legend" v-if="!isIDFMap"></div>
  </div>
</template>

<script>
import * as d3 from "d3";

export default {
  name: "MapComponent",
  props: {
    incidentsList: Array,
    workplannedList: Array,
    isIDFMap: Boolean,
    mapWidth: Number,
    mapHeight: Number,
    mapId: String,
    principalMap: String,
  },
  data() {
    return {
      isMapInitialized: false,
      width: 0,
      height: 0,
      path: null,
      projection: null,
      svg: null,
      svgLegend: null,
      tooltip: null,
      colorOk: "#3fc08e",
      colorWarning: "#f4bc06",
      colorError: "#df3423",
      departmentIncidents: [],
      departmentWorkplanned: [],
      isDataLoaded: false,
      displayButton: false,
      legend: null,
    };
  },
  mounted() {
    this.initializeMap();
  },
  methods: {
    initializeMap() {
      if (!this.isMapInitialized) {
        // Initialize width and height of the map
        if (window.document.body.clientWidth < 600) {
          this.width = window.document.body.clientWidth;
          this.height = window.document.body.clientWidth;
        } else {
          this.width = 550;
          this.height = 550;
        }

        if (this.isIDFMap) {
          this.width = this.mapWidth;
          this.height = this.mapHeight;
        }

        let scale =
          window.document.body.clientWidth < 600 ? this.width * 3.5 : 2700;

        // Handle geographic data'
        this.path = d3.geoPath();

        // Define projection
        if (!this.isIDFMap) {
          this.projection = d3
            .geoConicConformal()
            .center([2.454071, 47.279229]) // Center map for France
            .scale(scale);
        } else {
          this.projection = d3
            .geoConicConformal()
            .center([2.453071, 48.0892229]) // Center map for Ile de France
            .scale(5500);
        }

        if (!this.isIDFMap) {
          this.projection.translate([this.width / 2, this.height / 2]);
        } else {
          this.projection.translate([this.width / 2, this.height]);
        }

        // Add projection to path
        this.path.projection(this.projection);

        // Create svg element in div
        this.svg = d3
          .select(`#${this.mapId}`)
          .append("svg")
          .attr("id", `svg-${this.mapId}`)
          .attr("width", this.width)
          .attr("height", this.height);

        if (this.isIDFMap) {
          this.svg
            .append("text")
            .attr("x", 1)
            .attr("y", this.mapHeight - 10)
            .style("font-size", "12px")
            .text(this.$t("map.idfTitle"));
        }

        // Create a tooltip on the principal map to display department over the mouse pointer
        const svgPrincipal = d3.select(`#svg-${this.principalMap}`);
        this.tooltip = svgPrincipal
          .append("g")
          .attr("id", "tooltip")
          .style("display", "none");

        this.tooltip
          .append("polyline") // The rectangle containing the text, it is 230px width and 100px height
          .attr("points", "0,0 230,0 230,100 0,100 0,0")
          .style("fill", "#0017a4")
          .style("stroke", "black")
          .style("opacity", "0.9")
          .style("stroke-width", "1")
          .style("padding", "1em");

        this.tooltip
          .append("line") // A line inserted between department name and description
          .attr("x1", 40)
          .attr("y1", 25)
          .attr("x2", 200)
          .attr("y2", 25)
          .style("stroke", "#929292")
          .style("stroke-width", "0.5")
          .attr("transform", "translate(0, 5)");

        const text = this.tooltip
          .append("text") // Text that will contain all tspan (used for multilines)
          .style("font-size", "13px")
          .style("fill", "#fff")
          .attr("transform", "translate(10, 20)");

        text
          .append("tspan") // Department name udpated by its id
          .attr("x", 105) // ie, tooltip width / 2
          .attr("y", 0)
          .attr("id", "tooltip-department")
          .attr("text-anchor", "middle")
          .style("font-weight", "600")
          .style("font-size", "14px");

        text
          .append("tspan") // Fixed text
          .attr("x", 105) // ie, tooltip width / 2
          .attr("y", 30)
          .attr("text-anchor", "middle")
          .attr("id", "tooltip-ig")
          .style("fill", "#fff")
          .style("font-weight", "bold");

        text
          .append("tspan") // Fixed text
          .attr("x", 105) // ie, tooltip width / 2
          .attr("y", 60)
          .attr("text-anchor", "middle")
          .attr("id", "tooltip-tp")
          .style("fill", "#fff")
          .style("font-weight", "bold");

        // Define crosshatch to display planified workplanned and no incidents generics
        this.svg
          .append("defs")
          .append("pattern")
          .attr("id", "textureWorkplannedOK")
          .attr("patternUnits", "userSpaceOnUse")
          .attr("width", 5)
          .attr("height", 5)
          .append("path")
          .attr("d", "M5,0 10,10 0,10 Z")
          .attr("fill", this.colorOk)
          .attr("stroke", "#fff")
          .attr("stroke-width", 1);

        // Define crosshatch to display planified workplanned and incidents generics detected
        this.svg
          .append("defs")
          .append("pattern")
          .attr("id", "textureWorkplannedWarning")
          .attr("patternUnits", "userSpaceOnUse")
          .attr("width", 5)
          .attr("height", 5)
          .append("path")
          .attr("d", "M5,0 10,10 0,10 Z")
          .attr("fill", this.colorWarning)
          .attr("stroke", "#fff")
          .attr("stroke-width", 1);

        // Define crosshatch to display planified workplanned and incidents generics validated
        this.svg
          .append("defs")
          .append("pattern")
          .attr("id", "textureWorkplannedError")
          .attr("patternUnits", "userSpaceOnUse")
          .attr("width", 5)
          .attr("height", 5)
          .append("path")
          .attr("d", "M5,0 10,10 0,10 Z")
          .attr("fill", this.colorError)
          .attr("stroke", "#fff")
          .attr("stroke-width", 1);

        // Define crosshatch to display planified workplanned and incidents generics detected
        this.svg
          .append("defs")
          .append("pattern")
          .attr("id", "textureWorkplanned")
          .attr("patternUnits", "userSpaceOnUse")
          .attr("width", 5)
          .attr("height", 5)
          .append("path")
          .attr("d", "M5,0 10,10 0,10 Z")
          .attr("stroke", "#000")
          .attr("stroke-width", 1);

        if (!this.isIDFMap) {
          // Create legends
          const legendColors = [
            this.colorOk,
            this.colorWarning,
            this.colorError,
            "url(#textureWorkplanned)",
          ];
          const legendLabels = [
            this.$t("map.okLabel"),
            this.$t("map.warningLabel"),
            this.$t("map.errorLabel"),
            this.$t("map.okTpLabel"),
          ];
          const colorScale = d3
            .scaleOrdinal()
            .domain(legendLabels)
            .range(legendColors);
          const legendSpacing = 4;
          const legendRectSize = 20;

          this.svgLegend = d3
            .select("#legend")
            .append("svg")
            .attr("id", "svg-map")
            .attr("width", this.width)
            .attr("height", 100);

          this.legend = this.svgLegend
            .selectAll(".legend")
            .data(colorScale.domain())
            .enter()
            .append("g")
            .attr("class", "legend")
            .attr("transform", function (d, i) {
              const height = legendRectSize + legendSpacing;
              const horz = legendSpacing;
              const vert = i * height;
              return "translate(" + horz + "," + vert + ")";
            });

          this.legend
            .append("rect")
            .attr("width", legendRectSize)
            .attr("height", legendRectSize)
            .style("fill", colorScale)
            .style("stroke", colorScale);

          this.legend
            .append("text")
            .attr("x", legendRectSize + legendSpacing)
            .attr("y", legendRectSize - legendSpacing)
            .text(function (d) {
              return d;
            });
        }

        this.isMapInitialized = true;
      }
    },

    // Load tables for incidents and workplanned by department
    loadIncidentsAndWorkplanned() {
      if (this.incidentsList && this.incidentsList.length > 0) {
        this.departmentIncidents = [];
        let i = 0;
        this.incidentsList.forEach((incident) => {
          if (!incident.endDate) {
            incident.departments.forEach((dept) => {
              if (!this.departmentIncidents[i]) {
                this.departmentIncidents[i] = [];
              }
              const updateIncident = { ...incident };
              updateIncident.departments = [dept];
              this.departmentIncidents[i].push(updateIncident);
              i++;
            });
          }
        });

        this.isDataLoaded = true;
      }

      if (this.workplannedList && this.workplannedList.length > 0) {
        this.departmentWorkplanned = [];
        const currentTimestamp = Date.now();
        let i = 0;
        this.workplannedList.forEach((work) => {
          const timestampStart = new Date(
            work.dateBegin.replace(" ", "T")
          ).getTime();
          const timestampEnd = new Date(
            work.dateEnd.replace(" ", "T")
          ).getTime();

          if (
            (timestampStart < currentTimestamp &&
              timestampEnd > currentTimestamp) ||
            (timestampStart > currentTimestamp &&
              timestampStart - 24 * 60 * 60 * 1000 < currentTimestamp)
          ) {
            work.departments.forEach((dept) => {
              if (!this.departmentWorkplanned[i]) {
                this.departmentWorkplanned[i] = [];
              }
              const updateWork = { ...work };
              updateWork.departments = [dept];
              this.departmentWorkplanned[i].push(updateWork);
              i++;
            });
          }
        });

        this.isDataLoaded = true;
      }

      if (this.isDataLoaded) {
        this.loadMap();
      }
    },

    loadMap() {
      // Remove all features before drawing map
      this.svg.select("g").selectAll("path").remove();

      // Create svg group to add departments
      const deps = this.svg.append("g");
      let urlJSON = "./data/departments.json";
      if (this.isIDFMap) {
        urlJSON = "./data/iledefrance.json";
      }

      // Load IGN map of france
      d3.json(urlJSON).then((geojson) => {
        // Bind svg element for each "feature" of the geojson object
        const features = deps.selectAll("path").data(geojson.features);

        // Create each department with specific data
        features
          .enter()
          .append("path")
          .attr("class", "department")
          .style("stroke", "#005")
          .style("stroke-width", "0.1px")
          .attr("fill", (d) => {
            let found = "";
            let foundWorkplanned = false;
            for (let i = 0; i < this.departmentIncidents.length; i += 1) {
              if (
                this.departmentIncidents[i] &&
                this.departmentIncidents[i][0].departments[0] ===
                  d.properties.CODE_DEPT
              ) {
                found = this.departmentIncidents[i][0].status;
                break;
              }
            }

            for (let i = 0; i < this.departmentWorkplanned.length; i += 1) {
              if (
                this.departmentWorkplanned[i] &&
                this.departmentWorkplanned[i][0].departments[0] ===
                  d.properties.CODE_DEPT
              ) {
                foundWorkplanned = true;
                break;
              }
            }

            let selectedColor = foundWorkplanned
              ? "url(#textureWorkplannedOK)"
              : this.colorOk;
            if (found === "validated") {
              selectedColor = foundWorkplanned
                ? "url(#textureWorkplannedError)"
                : this.colorError;
            } else if (found === "detected") {
              selectedColor = foundWorkplanned
                ? "url(#textureWorkplannedWarning)"
                : this.colorWarning;
            }
            return selectedColor;
          })
          .attr("id", (d) => "d" + d.properties.CODE_DEPT)
          .attr("d", this.path)
          .on("click", this.departmentClickHandler)
          .on("mouseover", (d) => {
            if (d != undefined && d.target.__data__.properties) {
              const properties = d.target.__data__.properties;
              // Get selected departmentCode to filter list of incidents and workplanned and display detail
              const departmentCode = properties.CODE_DEPT;
              const departmentName = properties.NOM_DEPT;

              // Get nb incidents and nb workplanned for selected departement
              const { nbIg, nbTp } =
                this.filterListsByDepartments(departmentCode);
              this.tooltip.style("display", null);
              this.tooltip
                .select("#tooltip-department")
                .text(`${departmentCode} ${departmentName}`);
              this.tooltip
                .select("#tooltip-ig")
                .text(`${this.$tc("map.ig", nbIg, { nbIg })}`);
              this.tooltip
                .select("#tooltip-tp")
                .text(`${this.$tc("map.tp", nbTp, { nbTp })}`);
            }
          })
          .on("mouseout", () => {
            this.tooltip.style("display", "none");
          });
      });
    },
    filterListsByDepartments(deptCode) {
      let nbIg = 0;
      let nbTp = 0;

      // Count number of current generic incidents in the department
      this.incidentsList.forEach((incident) => {
        if (
          ["validated", "detected"].indexOf(incident.status) !== -1 &&
          incident.departments.indexOf(deptCode) !== -1
        ) {
          nbIg += 1;
        }
      });

      // Count number of workplanned are planned for today in the department
      this.workplannedList.forEach((work) => {
        const currentTimestamp = Date.now();
        const timestampStart = new Date(
          work.dateBegin.replace(" ", "T")
        ).getTime();
        const timestampEnd = new Date(work.dateEnd.replace(" ", "T")).getTime();

        if (work.departments.indexOf(deptCode) !== -1) {
          if (
            (timestampStart < currentTimestamp &&
              timestampEnd > currentTimestamp) ||
            (timestampStart > currentTimestamp &&
              timestampStart - 24 * 60 * 60 * 1000 < currentTimestamp)
          ) {
            nbTp += 1;
          }
        }
      });

      return { nbIg, nbTp };
    },

    departmentClickHandler(d) {
      if (d != undefined && d.target.__data__.properties) {
        const properties = d.target.__data__.properties;
        const departmentCode = properties.CODE_DEPT;
        d3.select(`d${departmentCode}`).style("fill", "#000");
        this.$emit("select-department", departmentCode);
      }
    },
  },
  watch: {
    incidentsList() {
      this.loadIncidentsAndWorkplanned();
    },
    workplannedList() {
      this.loadIncidentsAndWorkplanned();
    },
    departmentWorkplanned() {
      // Redraw map when workplanned list is updated
      this.loadMap();
    },
  },
};
</script>

<style>
.map-view {
  margin-bottom: 1rem;
}
.map-wrapper {
}
.department {
  cursor: pointer;
}
.department:hover {
  opacity: 0.7;
}
.legend {
  font-size: 12px;
}
rect {
  stroke-width: 2;
}
</style>